import { openDB } from "idb"

const versions = {
    orgMissionDb: 53,
    orgPlanItemDb: 53,
    orgActionDb: 53,
    orgActionItemDb: 53,
    orgExchangeRates: 53,
    orgHolidays: 53,
    orgLeavesDb: 53,
    orgResourceChart: 53,
    pmLeaves: 53,
    pmTimesheets: 54,
    timesheetMissionDb: 53,
    timesheetOrgDb: 53,
    timesheetMissionFullDb: 53,
    timesheetOrgFullDb: 53,
    orgClientsDb: 53,
    orgFinancialsDb: 53,
    orgClientsFullDb: 53,
    approvedTimesheetsDB: 53,
    secureKeys: 53,
}

export async function initDB(dbName, storeName, keyPath = "id") {
    const ver = versions[dbName]

    if (!ver) {
        throw new Error(`Version DB missing`)
    }

    const db = await openDB(dbName, ver, {
        upgrade(upgradeDB, oldVersion, newVersion, transaction) {
            // Delete all stores if version changed
            if (oldVersion < newVersion) {
                for (const name of upgradeDB.objectStoreNames) {
                    upgradeDB.deleteObjectStore(name)
                }
            }

            // Create store if it doesn't exist
            if (!upgradeDB.objectStoreNames.contains(storeName)) {
                upgradeDB.createObjectStore(storeName, { keyPath })
            }
        },
    })

    // Safety check
    if (!db.objectStoreNames.contains(storeName)) {
        throw new Error(`Store "${storeName}" was not created`)
    }

    return db
}

export const addDbItem = async (dbName, storeName, item) => {
    try {
        const db = await initDB(dbName, storeName) // keyPath is handled during initDB

        if (!item?.id) {
            // Ensure the item has an 'id' field
            throw new Error(`Item must have a unique identifier in "id".`)
        }

        await db.put(storeName, item)

        // Insert or update the item

        return item
    } catch (error) {
        throw error
    }
}

// Get an item by ID from the database with a dynamic store name
export const getDbItem = async (dbName, storeName, id) => {
    try {
        const db = await initDB(dbName, storeName)

        return await db.get(storeName, id) // Fetch item by ID
    } catch (error) {
        throw error
    }
}

// Get all items from the database with a dynamic store name
export const getAllItems = async (dbName, storeName) => {
    try {
        const db = await initDB(dbName, storeName)
        return await db.getAll(storeName) // Fetch all items
    } catch (error) {
        throw error
    }
}

// Delete an item by ID from the database with a dynamic store name
export const deleteDbItem = async (dbName, storeName, id) => {
    try {
        const db = await initDB(dbName, storeName)
        return await db.delete(storeName, id) // Delete item by ID
    } catch (error) {
        console.error("Failed to delete item:", error)
        throw error
    }
}

export const initDBWithIndexes = async (dbName, storeName, keyPath = "id", indexes = []) => {
    const ver = versions[dbName]

    return openDB(dbName, ver, {
        upgrade(db, oldVersion, newVersion, transaction) {
            let store

            if (!db.objectStoreNames.contains(storeName)) {
                // Create the store if it doesn't exist
                store = db.createObjectStore(storeName, { keyPath })
            } else if (oldVersion < newVersion) {
                // Clear the store's data when the version changes
                store = transaction.objectStore(storeName)
                store.clear()
            } else {
                // Get the store for index creation
                store = transaction.objectStore(storeName)
            }

            // Create indexes if provided
            indexes.forEach(({ name, keyPath, options }) => {
                if (!store.indexNames.contains(name)) {
                    store.createIndex(name, keyPath, options)
                }
            })
        },
    })
}

export const deleteIndexedDB = (dbName) => {
    return new Promise((resolve, reject) => {
        if (!dbName || typeof dbName !== "string") {
            reject(new Error("Invalid database name"))
            return
        }

        const request = indexedDB.deleteDatabase(dbName)

        request.onsuccess = () => {
            // ⚡ Some browsers resolve success even if DB never existed
            if (request.result === undefined) {
                resolve(`Database "${dbName}" did not exist, but operation is complete.`)
            } else {
                resolve(`Database "${dbName}" deleted successfully.`)
            }
        }

        request.onerror = (event) => {
            const message = event.target.error?.message || event.target.error || "Unknown error"
            reject(new Error(`Error deleting database "${dbName}": ${message}`))
        }

        request.onblocked = () => {
            reject(new Error(`Cannot delete "${dbName}": database is still open in another tab or connection.`))
        }
    })
}

// indexedDBUtils.js
export const deleteRecordsByUserId = (dbName, storeName, userId) => {
    return new Promise((resolve, reject) => {
        if (!dbName || !storeName || !userId) {
            reject(new Error("Missing required parameters: dbName, storeName, or userId"))
            return
        }

        const request = indexedDB.open(dbName)

        request.onerror = (event) => {
            reject(new Error(`Failed to open database: ${event.target.error?.message || event}`))
        }

        request.onsuccess = (event) => {
            const db = event.target.result

            // ✅ Guard: check if store exists
            if (!db.objectStoreNames.contains(storeName)) {
                db.close()
                reject(new Error(`Object store "${storeName}" not found in database "${dbName}"`))
                return
            }

            let deletedCount = 0
            const transaction = db.transaction([storeName], "readwrite")
            const store = transaction.objectStore(storeName)
            const cursorRequest = store.openCursor()

            cursorRequest.onsuccess = (event) => {
                const cursor = event.target.result
                if (cursor) {
                    if (String(cursor.key).includes(userId)) {
                        store.delete(cursor.key)
                        deletedCount++
                    }
                    cursor.continue()
                }
            }

            cursorRequest.onerror = (event) => {
                db.close()
                reject(new Error(`Error iterating store: ${event.target.error?.message || event}`))
            }

            transaction.oncomplete = () => {
                db.close()
                resolve(`Deleted ${deletedCount} record(s) for userId "${userId}" in store "${storeName}"`)
            }

            transaction.onerror = (event) => {
                db.close()
                reject(new Error(`Transaction failed: ${event.target.error?.message || event}`))
            }
        }

        request.onblocked = () => {
            reject(new Error(`Database "${dbName}" is blocked. Close all connections.`))
        }
    })
}
